import { useState, useEffect } from 'react';
import { Table, Button, Drawer, Space, Typography, message, Modal, Form, Input, Select, Radio, Tag, Switch } from 'antd';
import { EditOutlined, ClockCircleOutlined, ShopOutlined, PlusOutlined, EnvironmentOutlined, UserOutlined } from '@ant-design/icons';
import { api } from '../api';
import DelivererForm from './DelivererForm';
import OpeningHoursForm from './OpeningHoursForm';
import DelivererStoresForm from './DelivererStoresForm';
import AddressForm from './AddressForm';
import ContactForm from './ContactForm';

const { Text } = Typography;

export default function DeliverersTab({ isSupport }) {
  const [deliverers, setDeliverers] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [drawer, setDrawer]         = useState({ open: false, type: null, data: null, title: '' });
  const [createModal, setCreateModal] = useState(false);
  const [createMode, setCreateMode]   = useState('independent'); // 'independent' | 'from-store'
  const [stores, setStores]           = useState([]);
  const [creating, setCreating]       = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      setDeliverers(await api.deliverers.list());
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreateModal = async () => {
    form.resetFields();
    setCreateMode('independent');
    if (!stores.length) {
      try {
        setStores(await api.stores.list());
      } catch (err) {
        message.error(err.message);
      }
    }
    setCreateModal(true);
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setCreating(true);
      if (createMode === 'from-store') {
        await api.deliverers.createFromStore({
          store_id: values.store_id,
          calc_delivery_distance: values.calc_delivery_distance || null,
          calc_delivery_fee: values.calc_delivery_fee || null,
          accepts_geo_location: values.accepts_geo_location ?? false,
        });
      } else {
        await api.deliverers.create({
          name: values.name,
          calc_delivery_distance: values.calc_delivery_distance || null,
          calc_delivery_fee: values.calc_delivery_fee || null,
          accepts_geo_location: values.accepts_geo_location ?? false,
        });
      }
      message.success('Lieferant angelegt');
      setCreateModal(false);
      load();
    } catch (err) {
      if (err?.errorFields) return; // validation error, stay open
      message.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const openAddressDrawer = async (deliverer) => {
    if (!deliverer.address_id) { message.warning('Keine Adresse verknüpft.'); return; }
    try {
      const addr = await api.addresses.get(deliverer.address_id);
      setDrawer({ open: true, type: 'address', data: addr, title: `Adresse – ${deliverer.name}` });
    } catch (err) { message.error(err.message); }
  };

  const openContactDrawer = async (deliverer) => {
    try {
      let contact;
      if (deliverer.contact_id) {
        contact = await api.contacts.get(deliverer.contact_id);
      } else {
        contact = await api.deliverers.createContact(deliverer.id);
        await load();
      }
      setDrawer({ open: true, type: 'contact', data: contact, title: `Kontakt – ${deliverer.name}` });
    } catch (err) { message.error(err.message); }
  };

  const openOhDrawer = async (deliverer) => {
    try {
      let ohId = deliverer.opening_hours_id;
      if (!ohId) {
        const oh = await api.openingHours.create({ name: deliverer.name, open_json: [] });
        await api.deliverers.update(deliverer.id, {
          name: deliverer.name,
          opening_hours_id: oh.id,
          calc_delivery_distance: deliverer.calc_delivery_distance,
          calc_delivery_fee: deliverer.calc_delivery_fee,
          accepts_geo_location: deliverer.accepts_geo_location,
        });
        await load();
        ohId = oh.id;
      }
      const oh = await api.openingHours.get(ohId);
      setDrawer({ open: true, type: 'oh', data: oh, title: `Öffnungszeiten – ${deliverer.name}` });
    } catch (err) {
      message.error(err.message);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, d) => (
        <Space size={6}>
          {name}
          {d.store_id && (
            <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>
              {d.store_name || 'Store'}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Status',
      key: 'is_active',
      width: 130,
      render: (_, d) => (
        <Switch
          checked={Boolean(d.is_active)}
          checkedChildren="Aktiv"
          unCheckedChildren="Pausiert"
          onChange={async (checked) => {
            try {
              await api.deliverers.setActive(d.id, checked);
              setDeliverers(prev => prev.map(x => x.id === d.id ? { ...x, is_active: checked } : x));
            } catch (err) {
              message.error(err.message);
            }
          }}
        />
      ),
    },
    {
      title: 'Lieferradius',
      dataIndex: 'calc_delivery_distance',
      key: 'dist',
      width: 120,
      render: v => <Text style={{ fontSize: 12, fontFamily: 'monospace' }}>{v || '–'}</Text>,
    },
    {
      title: '',
      key: 'actions',
      width: 210,
      render: (_, d) => (
        <Space>
          {isSupport && (
            <Button
              size="small"
              icon={<ShopOutlined />}
              onClick={() => setDrawer({ open: true, type: 'stores', data: d, title: `Stores – ${d.name}` })}
            >
              Stores
            </Button>
          )}
          {!d.store_id && (
            <>
              <Button size="small" icon={<EnvironmentOutlined />} onClick={() => openAddressDrawer(d)}>
                Adresse
              </Button>
              <Button size="small" icon={<UserOutlined />} onClick={() => openContactDrawer(d)}>
                Kontakt
              </Button>
            </>
          )}
          <Button
            size="small"
            icon={<ClockCircleOutlined />}
            onClick={() => openOhDrawer(d)}
          >
            Zeiten
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => setDrawer({ open: true, type: 'edit', data: d, title: `Lieferant – ${d.name}` })}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      {isSupport && (
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            Neuer Lieferant
          </Button>
        </div>
      )}

      <Table rowKey="id" columns={columns} dataSource={deliverers} loading={loading} pagination={false} size="middle" />

      {/* Create Modal */}
      <Modal
        title="Neuer Lieferant"
        open={createModal}
        onOk={handleCreate}
        onCancel={() => setCreateModal(false)}
        okText="Anlegen"
        cancelText="Abbrechen"
        confirmLoading={creating}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item label="Art des Lieferanten">
            <Radio.Group value={createMode} onChange={e => { setCreateMode(e.target.value); form.resetFields(['name', 'store_id']); }}>
              <Radio value="independent">Unabhängiger Lieferant</Radio>
              <Radio value="from-store">Aus Store erzeugen</Radio>
            </Radio.Group>
          </Form.Item>

          {createMode === 'independent' ? (
            <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name ist erforderlich' }]}>
              <Input placeholder="z.B. Fahrrad Kurier GmbH" />
            </Form.Item>
          ) : (
            <Form.Item name="store_id" label="Store" rules={[{ required: true, message: 'Bitte Store auswählen' }]}>
              <Select
                showSearch
                placeholder="Store auswählen…"
                optionFilterProp="label"
                options={stores.map(s => ({ value: s.id, label: s.name }))}
              />
            </Form.Item>
          )}

          <Form.Item name="calc_delivery_distance" label="Lieferradius (Formel)">
            <Input placeholder="z.B. 5000" />
          </Form.Item>
          <Form.Item name="calc_delivery_fee" label="Liefergebühr (Formel)">
            <Input placeholder="z.B. 2.50" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Drawer */}
      <Drawer
        title={drawer.title}
        open={drawer.open}
        onClose={() => setDrawer(d => ({ ...d, open: false }))}
        width={520}
        destroyOnClose
      >
        {drawer.type === 'edit' && drawer.data && (
          <DelivererForm
            deliverer={drawer.data}
            onSaved={() => { setDrawer(d => ({ ...d, open: false })); load(); }}
          />
        )}
        {drawer.type === 'oh' && drawer.data && (
          <OpeningHoursForm
            openingHours={drawer.data}
            onSaved={() => setDrawer(d => ({ ...d, open: false }))}
          />
        )}
        {drawer.type === 'stores' && drawer.data && (
          <DelivererStoresForm
            deliverer={drawer.data}
            onSaved={() => { setDrawer(d => ({ ...d, open: false })); load(); }}
          />
        )}
        {drawer.type === 'address' && drawer.data && (
          <AddressForm
            address={drawer.data}
            onSaved={() => setDrawer(d => ({ ...d, open: false }))}
          />
        )}
        {drawer.type === 'contact' && drawer.data && (
          <ContactForm
            contact={drawer.data}
            onSaved={() => setDrawer(d => ({ ...d, open: false }))}
          />
        )}
      </Drawer>
    </>
  );
}
